import { forwardRef } from 'react';
import { Dumbbell } from 'lucide-react';
import straivLogo from '@/assets/straiv-logo-share.png';
import shareBoldBg from '@/assets/share-bold-bg.jpg';
import { ShareableWorkout } from '../ShareWorkoutCard';
import { RunningRouteVector, RouteCoordinate } from './RunningRouteVector';

interface ShareTemplateProps {
  workout: ShareableWorkout;
}

// Exact format for share image - no rounding, 1 decimal only when fractional
const formatTimeExact = (totalSeconds: number) => {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  const secRounded = Math.round(s);
  const secStr = Math.abs(s - secRounded) < 0.01 ? String(secRounded).padStart(2, '0') : s.toFixed(1);
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${secStr}`;
  return `${m}:${secStr}`;
};
// Pace: always whole seconds (0:37 not 0:37.5)
const formatPaceExact = (paceSeconds: number) => {
  if (!paceSeconds || !isFinite(paceSeconds) || paceSeconds <= 0) return '--:--';
  const m = Math.floor(paceSeconds / 60);
  const s = Math.round(paceSeconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
};

// Add proper spacing to workout names: "ChestDay" -> "Chest Day", "UPPER_BODY" -> "Upper Body"
const formatShareTitle = (title: string) => {
  if (!title?.trim()) return title;
  const s = title
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .trim();
  return s.split(/\s+/).map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
};

export const ShareTemplateBold = forwardRef<HTMLDivElement, ShareTemplateProps>(
  ({ workout }, ref) => {
    const formatTime = (minutes: number) => {
      const h = Math.floor(minutes / 60);
      const m = Math.floor(minutes % 60);
      const s = Math.round((minutes % 1) * 60);
      if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
      return `${m}:${String(s).padStart(2, '0')}`;
    };

    const formatDate = (dateString: string) => {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    };

    const isRunning = workout.workout_type?.toLowerCase() === 'running';
    const routeCoordinates: RouteCoordinate[] = (workout as any).route_coordinates || [];

    return (
      <div
        ref={ref}
        className="overflow-hidden flex flex-col items-center"
        style={{
          width: '1080px',
          height: '1980px',
          backgroundImage: `url(${shareBoldBg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        {/* Header with Logo */}
        <div style={{ padding: '80px 80px 48px' }}>
          <div className="flex justify-center">
            <img src={straivLogo} alt="STRAIV" style={{ width: '400px', height: 'auto' }} />
          </div>
        </div>

        {/* Main content area */}
        <div className="relative">

          <div className="relative" style={{ padding: '24px 80px 0' }}>
            {/* Stats Card - gym only */}
            {!isRunning && (
              <div 
                className="mb-8"
                style={{
                  background: 'linear-gradient(135deg, rgba(40,40,45,0.95) 0%, rgba(50,50,55,0.95) 100%)',
                  borderRadius: '40px',
                  padding: '48px',
                }}
              >
                <div className="grid grid-cols-3 gap-8 text-center">
                  <div>
                    <p className="mb-2" style={{ fontSize: '28px', color: 'rgba(255,255,255,0.5)' }}>Sets</p>
                    <p className="font-bold" style={{ fontSize: '56px', color: '#0AFFC2' }}>
                      {workout.total_sets || 0}
                    </p>
                  </div>
                  <div>
                    <p className="mb-2" style={{ fontSize: '28px', color: 'rgba(255,255,255,0.5)' }}>Volume</p>
                    <p className="font-bold" style={{ fontSize: '56px', color: '#0AFFC2' }}>
                      {workout.total_volume ? Math.round(workout.total_volume) : 0} kg
                    </p>
                  </div>
                  <div>
                    <p className="mb-2" style={{ fontSize: '28px', color: 'rgba(255,255,255,0.5)' }}>Time</p>
                    <p className="font-bold" style={{ fontSize: '56px', color: '#0AFFC2' }}>
                      {formatTime(workout.duration_minutes)}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Title Section */}
            <div className="text-center" style={{ marginBottom: '24px' }}>
              <h2 className="font-bold mb-4" style={{ fontSize: '56px', color: '#0AFFC2' }}>
                {formatShareTitle(workout.title)}
              </h2>
              <p style={{ fontSize: '32px', color: 'rgba(255,255,255,0.5)' }}>
                {formatDate(workout.completed_at)}
              </p>
            </div>
          </div>
        </div>

        {/* Running route + stats (or stats only when no route) */}
        {isRunning && (
          <div className="flex flex-col items-center" style={{ padding: '0 80px 24px' }}>
            {routeCoordinates.length >= 2 && (
              <RunningRouteVector
                coordinates={routeCoordinates}
                width={700}
                height={500}
                strokeColor="#0AFFC2"
                strokeWidth={3}
                showStartEnd={true}
              />
            )}
            <div className="grid grid-cols-4 gap-6 text-center w-full mt-8">
              <div>
                <p className="uppercase tracking-wider" style={{ fontSize: '24px', color: 'rgba(255,255,255,0.5)' }}>Distance</p>
                <p className="font-bold" style={{ fontSize: '44px', color: '#0AFFC2' }}>{workout.distance_km || '0'}</p>
              </div>
              <div>
                <p className="uppercase tracking-wider" style={{ fontSize: '24px', color: 'rgba(255,255,255,0.5)' }}>Pace</p>
                <p className="font-bold" style={{ fontSize: '44px', color: '#0AFFC2' }}>
                  {(workout as any).pace_seconds_per_km != null ? formatPaceExact((workout as any).pace_seconds_per_km) : (workout.average_pace || '--:--')}
                </p>
              </div>
              <div>
                <p className="uppercase tracking-wider" style={{ fontSize: '24px', color: 'rgba(255,255,255,0.5)' }}>Time</p>
                <p className="font-bold" style={{ fontSize: '44px', color: '#0AFFC2' }}>
                  {(workout as any).duration_seconds != null ? formatTimeExact((workout as any).duration_seconds) : formatTime(workout.duration_minutes)}
                </p>
              </div>
              <div>
                <p className="uppercase tracking-wider" style={{ fontSize: '24px', color: 'rgba(255,255,255,0.5)' }}>Calories</p>
                <p className="font-bold" style={{ fontSize: '44px', color: '#0AFFC2' }}>{workout.calories_burned || '--'}</p>
              </div>
            </div>
          </div>
        )}

        {/* Exercises List (gym only) - grey card */}
        {!isRunning && workout.exercises && workout.exercises.length > 0 && (
          <div style={{ padding: '0 80px 12px' }}>
            <div 
              style={{ 
                background: 'hsl(var(--muted) / 0.92)',
                borderRadius: '32px',
                padding: '28px 32px',
              }}
            >
              <div className="space-y-5">
                {workout.exercises.slice(0, 4).map((exercise, index) => (
                  <div 
                    key={index}
                    className="flex items-center gap-6"
                  >
                    <Dumbbell style={{ width: '36px', height: '36px', color: 'hsl(var(--primary))', flexShrink: 0 }} />
                    <span className="flex-1 font-medium" style={{ fontSize: '36px', color: 'hsl(var(--foreground))' }}>{formatShareTitle(exercise.name)}</span>
                    <span style={{ fontSize: '32px', color: 'hsl(var(--muted-foreground))', flexShrink: 0 }}>
                      {exercise.sets}×{exercise.reps || '-'}
                    </span>
                  </div>
                ))}
                {workout.exercises.length > 4 && (
                  <p className="text-center pt-1" style={{ fontSize: '28px', color: 'hsl(var(--muted-foreground))' }}>
                    +{workout.exercises.length - 4} more exercises
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="text-center" style={{ padding: '16px 48px 48px' }}>
          <p className="font-medium" style={{ fontSize: '32px', color: 'rgba(255,255,255,0.4)' }}>
            Powered by STRAIV AI
          </p>
        </div>
      </div>
    );
  }
);

ShareTemplateBold.displayName = 'ShareTemplateBold';
